import { useEffect, useMemo, useState } from 'react';
import { useIsMobile } from '../lib/useIsMobile';
import type { Identity, PrefBag, PrefWriteBag, Signature } from '@zimbra-webif/sdk';
import { getLoadExternalImages, setLoadExternalImages } from '../lib/prefs';
import {
  useAccountInfo,
  useCreateSignature,
  useDeleteSignature,
  useIdentities,
  useModifySignature,
  usePrefs,
  useSavePrefs,
  useSaveVacation,
  useSignatures,
  useVacation,
  type VacationForm,
} from '../lib/settings-queries';
import './settings.css';

type TabKey = 'general' | 'signatures' | 'identities' | 'vacation';

const TABS: Array<{ key: TabKey; label: string }> = [
  { key: 'general', label: 'General' },
  { key: 'signatures', label: 'Signatures' },
  { key: 'identities', label: 'Identities' },
  { key: 'vacation', label: 'Vacation' },
];

/** Read the first string value of a (possibly multi-valued) pref. */
function one(v: string | string[] | undefined): string {
  if (v === undefined) return '';
  return Array.isArray(v) ? (v[0] ?? '') : v;
}

/** Plain-text body of a signature, if any. */
function plainOf(sig: Signature): string {
  const parts = sig.content ?? [];
  const plain = parts.find((p) => p.type === 'text/plain') ?? parts[0];
  return plain?._content ?? '';
}

/* ========================================================================== */
/* General                                                                     */
/* ========================================================================== */

function GeneralTab() {
  const prefsQ = usePrefs();
  const infoQ = useAccountInfo();
  const save = useSavePrefs();

  const [display, setDisplay] = useState('');
  const [fromAddr, setFromAddr] = useState('');
  const [perPage, setPerPage] = useState('');
  const [loaded, setLoaded] = useState<PrefBag | null>(null);

  useEffect(() => {
    if (!prefsQ.data) return;
    const bag = prefsQ.data;
    setDisplay(one(bag.zimbraPrefFromDisplay));
    setFromAddr(one(bag.zimbraPrefFromAddress));
    setPerPage(one(bag.zimbraPrefMailItemsPerPage) || '25');
    setLoaded(bag);
  }, [prefsQ.data]);

  const accountName =
    typeof infoQ.data?.name === 'string' ? infoQ.data.name : undefined;

  function onSave() {
    if (!loaded) return;
    const changes: PrefWriteBag = {};
    if (display !== one(loaded.zimbraPrefFromDisplay)) changes.zimbraPrefFromDisplay = display;
    if (fromAddr !== one(loaded.zimbraPrefFromAddress)) changes.zimbraPrefFromAddress = fromAddr;
    if (perPage !== one(loaded.zimbraPrefMailItemsPerPage)) {
      changes.zimbraPrefMailItemsPerPage = perPage;
    }
    if (Object.keys(changes).length === 0) return;
    save.mutate(changes);
  }

  if (prefsQ.isLoading) return <div className="loading">Loading…</div>;

  const [loadImages, setLoadImages] = useState(getLoadExternalImages());

  return (
    <>
      <div className="set-section-hdr">
        <div>
          <h2>General</h2>
          <div className="sub">Display name, default sending address and list size.</div>
        </div>
      </div>

      <div className="set-field">
        <label htmlFor="set-display">Display name</label>
        <input
          id="set-display"
          className="set-input"
          value={display}
          onChange={(e) => setDisplay(e.target.value)}
          placeholder="Your name as recipients see it"
        />
      </div>

      <div className="set-field">
        <label htmlFor="set-from">Default from address</label>
        <input
          id="set-from"
          className="set-input"
          value={fromAddr}
          onChange={(e) => setFromAddr(e.target.value)}
          placeholder={accountName ?? 'you@example.com'}
        />
        {accountName ? (
          <div className="set-saved">Account address: {accountName}</div>
        ) : null}
      </div>

      <div className="set-field">
        <label htmlFor="set-perpage">Items per page</label>
        <select
          id="set-perpage"
          className="set-select"
          value={perPage}
          onChange={(e) => setPerPage(e.target.value)}
        >
          {['10', '25', '50', '100'].map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
      </div>

      <div className="set-field">
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={loadImages}
            onChange={(e) => {
              setLoadImages(e.target.checked);
              setLoadExternalImages(e.target.checked);
            }}
          />
          Load external images in messages
        </label>
        <div className="sub">On by default. Turn off to block remote images (privacy / tracking pixels).</div>
      </div>

      <div className="set-actions">
        <button className="btn-primary" onClick={onSave} disabled={save.isPending}>
          {save.isPending ? 'Saving…' : 'Save changes'}
        </button>
        {save.isSuccess ? <span className="set-saved">Saved.</span> : null}
        {save.isError ? <span className="set-saved">Save failed.</span> : null}
      </div>
    </>
  );
}

/* ========================================================================== */
/* Signatures                                                                  */
/* ========================================================================== */

function SignaturesTab() {
  const sigsQ = useSignatures();
  const create = useCreateSignature();
  const modify = useModifySignature();
  const remove = useDeleteSignature();

  // null = nothing selected; '' = drafting a new signature; else an existing id.
  const [selId, setSelId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [text, setText] = useState('');

  const sigs = useMemo(() => sigsQ.data ?? [], [sigsQ.data]);

  function selectExisting(sig: Signature) {
    setSelId(sig.id ?? null);
    setName(sig.name ?? '');
    setText(plainOf(sig));
  }

  function startNew() {
    setSelId('');
    setName('');
    setText('');
  }

  function onSave() {
    if (selId === null) return;
    if (selId === '') {
      create.mutate(
        { name: name || 'Untitled', text },
        { onSuccess: (sig) => setSelId(sig.id ?? null) },
      );
    } else {
      modify.mutate({ id: selId, name: name || 'Untitled', text });
    }
  }

  function onDelete() {
    if (selId && selId !== '') {
      remove.mutate(selId, {
        onSuccess: () => {
          setSelId(null);
          setName('');
          setText('');
        },
      });
    }
  }

  const busy = create.isPending || modify.isPending;

  return (
    <>
      <div className="set-section-hdr">
        <div>
          <h2>Signatures</h2>
          <div className="sub">Plain-text signatures you can append to outgoing mail.</div>
        </div>
        <button className="btn-primary" onClick={startNew}>
          New signature
        </button>
      </div>

      {sigsQ.isLoading ? (
        <div className="loading">Loading…</div>
      ) : (
        <div className="set-master">
          <div className="set-list">
            {sigs.length === 0 ? (
              <div className="set-empty">No signatures yet.</div>
            ) : (
              sigs.map((sig) => (
                <button
                  key={sig.id ?? sig.name}
                  className={`set-list-item${selId !== '' && selId === sig.id ? ' active' : ''}`}
                  onClick={() => selectExisting(sig)}
                >
                  <span className="name">{sig.name || '(unnamed)'}</span>
                </button>
              ))
            )}
          </div>

          {selId === null ? (
            <div className="set-empty">Select a signature, or create a new one.</div>
          ) : (
            <div className="set-editor">
              <h3>{selId === '' ? 'New signature' : 'Edit signature'}</h3>
              <div className="set-field">
                <label htmlFor="sig-name">Name</label>
                <input
                  id="sig-name"
                  className="set-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
              <div className="set-field">
                <label htmlFor="sig-text">Signature text</label>
                <textarea
                  id="sig-text"
                  className="set-textarea"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                />
              </div>
              <div className="set-actions">
                <button className="btn-primary" onClick={onSave} disabled={busy}>
                  {busy ? 'Saving…' : 'Save'}
                </button>
                {selId !== '' ? (
                  <button
                    className="ghost"
                    onClick={onDelete}
                    disabled={remove.isPending}
                  >
                    Delete
                  </button>
                ) : null}
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}

/* ========================================================================== */
/* Identities (read-only)                                                      */
/* ========================================================================== */

function IdentitiesTab() {
  const identsQ = useIdentities();
  const [selId, setSelId] = useState<string | null>(null);

  const idents = useMemo(() => identsQ.data ?? [], [identsQ.data]);
  const selected: Identity | undefined =
    idents.find((i) => (i.id ?? i.name) === selId) ?? idents[0];

  const attrs: PrefBag = selected?._attrs ?? {};

  return (
    <>
      <div className="set-section-hdr">
        <div>
          <h2>Identities</h2>
          <div className="sub">Send-as personas configured on this account (read-only).</div>
        </div>
      </div>

      {identsQ.isLoading ? (
        <div className="loading">Loading…</div>
      ) : idents.length === 0 ? (
        <div className="set-empty">No identities.</div>
      ) : (
        <div className="set-master">
          <div className="set-list">
            {idents.map((id) => {
              const key = id.id ?? id.name ?? '';
              const isActive = (selId ?? (idents[0]?.id ?? idents[0]?.name)) === key;
              return (
                <button
                  key={key}
                  className={`set-list-item${isActive ? ' active' : ''}`}
                  onClick={() => setSelId(key)}
                >
                  <span className="name">{id.name || '(unnamed)'}</span>
                </button>
              );
            })}
          </div>

          {selected ? (
            <div className="set-editor">
              <h3>{selected.name || '(unnamed)'}</h3>
              <div className="set-kv">
                <span className="k">Identity name</span>
                <span className="v">{selected.name ?? '—'}</span>
                <span className="k">From display</span>
                <span className="v">{one(attrs.zimbraPrefFromDisplay) || '—'}</span>
                <span className="k">From address</span>
                <span className="v">{one(attrs.zimbraPrefFromAddress) || '—'}</span>
                <span className="k">Reply-to address</span>
                <span className="v">{one(attrs.zimbraPrefReplyToAddress) || '—'}</span>
                <span className="k">Default signature</span>
                <span className="v">{one(attrs.zimbraPrefDefaultSignatureId) || '—'}</span>
              </div>
            </div>
          ) : null}
        </div>
      )}
    </>
  );
}

/* ========================================================================== */
/* Vacation / out-of-office                                                    */
/* ========================================================================== */

function VacationTab() {
  const vacQ = useVacation();
  const save = useSaveVacation();

  const [form, setForm] = useState<VacationForm>({
    enabled: false,
    reply: '',
    fromDate: '',
    untilDate: '',
  });

  useEffect(() => {
    if (!vacQ.data) return;
    const v = vacQ.data;
    setForm({
      enabled: v.enabled ?? false,
      reply: v.reply ?? '',
      fromDate: v.fromDate ?? '',
      untilDate: v.untilDate ?? '',
    });
  }, [vacQ.data]);

  if (vacQ.isLoading) return <div className="loading">Loading…</div>;

  return (
    <>
      <div className="set-section-hdr">
        <div>
          <h2>Vacation</h2>
          <div className="sub">Out-of-office auto-reply for incoming mail.</div>
        </div>
      </div>

      <label className="set-toggle">
        <input
          type="checkbox"
          checked={form.enabled}
          onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
        />
        <span className="lbl">Send automatic replies</span>
      </label>

      <div className="set-field">
        <label htmlFor="vac-reply">Auto-reply message</label>
        <textarea
          id="vac-reply"
          className="set-textarea"
          value={form.reply}
          disabled={!form.enabled}
          onChange={(e) => setForm({ ...form, reply: e.target.value })}
          placeholder="I'm currently out of the office…"
        />
      </div>

      <div className="set-row">
        <div className="set-field">
          <label htmlFor="vac-from">Start (yyyyMMddHHmmssZ, optional)</label>
          <input
            id="vac-from"
            className="set-input"
            value={form.fromDate}
            disabled={!form.enabled}
            onChange={(e) => setForm({ ...form, fromDate: e.target.value })}
            placeholder="20260616000000Z"
          />
        </div>
        <div className="set-field">
          <label htmlFor="vac-until">End (yyyyMMddHHmmssZ, optional)</label>
          <input
            id="vac-until"
            className="set-input"
            value={form.untilDate}
            disabled={!form.enabled}
            onChange={(e) => setForm({ ...form, untilDate: e.target.value })}
            placeholder="20260630000000Z"
          />
        </div>
      </div>

      <div className="set-actions">
        <button
          className="btn-primary"
          onClick={() => save.mutate(form)}
          disabled={save.isPending}
        >
          {save.isPending ? 'Saving…' : 'Save changes'}
        </button>
        {save.isSuccess ? <span className="set-saved">Saved.</span> : null}
        {save.isError ? <span className="set-saved">Save failed.</span> : null}
      </div>
    </>
  );
}

/* ========================================================================== */
/* Shell                                                                       */
/* ========================================================================== */

export function SettingsScreen() {
  const [tab, setTab] = useState<TabKey>('general');
  const isMobile = useIsMobile();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const tabLabel = TABS.find((t) => t.key === tab)?.label ?? 'Settings';

  return (
    <div className={`settings${isMobile ? ' m-shell' : ''}${drawerOpen ? ' m-drawer' : ''}`}>
      {isMobile && drawerOpen ? (
        <div className="m-backdrop" onClick={() => setDrawerOpen(false)} />
      ) : null}
      <aside className="pane set-rail m-side">
        <div className="pane-hdr">Settings</div>
        <div className="pane-scroll">
          {TABS.map((t) => (
            <button
              key={t.key}
              className={`set-tab${tab === t.key ? ' active' : ''}`}
              onClick={() => {
                setTab(t.key);
                setDrawerOpen(false);
              }}
            >
              <span className="dot" />
              {t.label}
            </button>
          ))}
        </div>
      </aside>

      <section className="pane set-content m-list-pane">
        <div className="m-topbar">
          <button className="m-burger" aria-label="Settings menu" onClick={() => setDrawerOpen(true)}>
            ☰
          </button>
          <span>{tabLabel}</span>
        </div>
        <div className="pane-scroll">
          {tab === 'general' ? <GeneralTab /> : null}
          {tab === 'signatures' ? <SignaturesTab /> : null}
          {tab === 'identities' ? <IdentitiesTab /> : null}
          {tab === 'vacation' ? <VacationTab /> : null}
        </div>
      </section>
    </div>
  );
}
